"use client";
import React, { useState, useMemo, startTransition } from 'react';
import { Box, Text } from "@chakra-ui/react";
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from "lucide-react";
import NewBackground_2 from '../../../../assets/glb/NewBackground_2.jpg'

const DoorToast = ({ 
  message, 
  onClick 
}: { 
  message: string;
  onClick: () => void;
}) => (
  <Box
    position="absolute"
    top="-68px"
    left="50%"
    transform="translateX(-50%)"
    minW="160px"
    maxW="260px"
    px="16px"
    py="12px"
    textAlign="center"
    borderRadius="10px"
    fontSize="12px"
    fontWeight="600"
    letterSpacing="0.4px"
    whiteSpace="normal"
    zIndex={1000}
    bg="rgba(10, 15, 30, 0.9)"
    color="#3CFFC6"
    border="2px solid #3CFFC6"
    backdropFilter="blur(6px)"
    boxShadow="
      0 0 10px rgba(60,255,198,0.8),
      0 0 25px rgba(60,255,198,0.5),
      inset 0 0 10px rgba(60,255,198,0.4)
    "
    cursor="pointer"
    transition="all 0.3s ease"
    _hover={{
      transform: "translateX(-50%) scale(1.05)",
      boxShadow: `
        0 0 15px rgba(60,255,198,1),
        0 0 35px rgba(60,255,198,0.7),
        inset 0 0 15px rgba(60,255,198,0.6)
      `
    }}
    onClick={onClick}
  >
    {message}

    <Box
      position="absolute"
      bottom="-12px"
      left="50%"
      transform="translateX(-50%) rotate(45deg)"
      width="18px"
      height="18px"
      bg="rgba(10, 15, 30, 0.95)"
      borderRight="2px solid #3CFFC6"
      borderBottom="2px solid #3CFFC6"
      boxShadow="0 0 10px rgba(60,255,198,0.8)"
    />
  </Box>
);

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "60, 255, 198"; // Matches #3CFFC6
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};

// Navigation Arrow Component
const NavigationArrow: React.FC<{
  direction: "left" | "right";
  onClick: () => void;
  primaryColor: string;
}> = ({ direction, onClick, primaryColor }) => {
  const primaryColorRgb = hexToRgb(primaryColor);
  
  return (
    <button
      onClick={onClick}
      className="w-[3.75rem] h-10 py-2 px-4 rounded-3xl transition-all duration-200 hover:scale-110 flex items-center justify-center"
      style={{
        background: `linear-gradient(${
          direction === "left" ? "275.041deg" : "90deg"
        },
              rgba(${primaryColorRgb}, 0.6) 7.25%, rgba(${primaryColorRgb}, 0.15) 84.803%)`,
        border: `1px solid ${primaryColor}`,
        boxShadow: `0px 0px 15.9542px 0px rgba(${primaryColorRgb}, 0.3)`,
        color: 'white',
        cursor: 'pointer',
      }}
    >
      {direction === "left" ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </button>
  );
};

const SkillSet: React.FC<{
  data?: any;
  preloadedAssets?: any;
  setCurrentScreenId?: (screenId: number) => void;
}> = React.memo(({ data, preloadedAssets, setCurrentScreenId }) => {
  const [showNugget, setShowNugget] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  
  const primaryColor = "#3CFFC6"; // Green color matching the toast
  const primaryColorRgb = useMemo(() => hexToRgb(primaryColor), [primaryColor]);
  
  // Your specific URL
  const NUGGET_URL = "https://leadupp.co/Atlantis/Leap/goaloverview.html";
  
  const backgroundImageUrl = NewBackground_2;
    
  // Handler for back navigation from Nugget
  const handleBackNavigation = () => {
    setShowNugget(false);
  };

  // Handler for next navigation from Nugget - FIXED with startTransition
  const handleNextNavigation = () => {
    if (isNavigating) return; // Prevent multiple clicks
    
   
    
    // Check if the function exists and is callable
    if (setCurrentScreenId && typeof setCurrentScreenId === 'function') {
     
      
      // Use startTransition to wrap the state update
      startTransition(() => {
        setIsNavigating(true);
        
        try {
          // Call the function to navigate to screen 2
          setCurrentScreenId(2);
        
          
          // Don't setShowNugget(false) - let parent handle the transition
        } catch (error) {
          console.error("❌ Error calling setCurrentScreenId:", error);
          setIsNavigating(false);
        }
      });
    } else {
      console.error("❌ setCurrentScreenId is NOT a function or is undefined!");
      
      // Fallback: just close the nugget without transition
      setShowNugget(false);
    }
  };

  // Handle toast click
  const handleToastClick = () => {
    setShowNugget(true);
    setIsLoading(true);
    setIframeError(false);
  };

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setIsLoading(false);
    setIframeError(true);
  };

  return (
    <div
      className="relative w-full h-screen overflow-hidden bg-no-repeat bg-center bg-cover flex items-center justify-center"
      style={{ backgroundImage: `url('${backgroundImageUrl}')` }}
    >
      {/* Loading overlay for navigation */}
      {isNavigating && (
        <div className="absolute inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-70">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-[#3CFFC6] rounded-full animate-spin mb-4"></div>
            <Text color="#3CFFC6" fontSize="lg" fontWeight="bold">
              Navigating to next screen...
            </Text>
          </div>
        </div>
      )}

      {/* Show either the toast or the Nugget popup */}
      {!showNugget ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <DoorToast 
            message="Click Here to Overview!" 
            onClick={handleToastClick}
          />
          
        
        </motion.div>
      ) : (
        // When popup is open, show CLEAR background (no blur overlay)
        <div
          className="absolute inset-0 z-50 flex items-center justify-center p-4"
          style={{ 
            backgroundImage: `url('${backgroundImageUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Nugget Content - Reduced height to avoid overlapping */}
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
            className="relative w-full max-w-[54.875rem] overflow-hidden rounded-[2.375rem]"
            style={{
              // Reduced height to avoid overlapping with top menu
              maxWidth: '54.875rem',
              height: '75vh', // Reduced from 85vh to 75vh
              maxHeight: '75vh',
              marginTop: '80px', // Added margin to push it down from top
            }}
          >
            <div
              className="relative flex flex-col items-center justify-start gap-5 h-full w-full rounded-[2.375rem] px-10 py-8 backdrop-blur-2xl backdrop-filter"
              style={{
                background: `linear-gradient(21deg, rgba(0, 0, 0, 0.80) -10.76%, rgba(${primaryColorRgb}, 0.2) 107.18%)`,
                boxShadow: `0px 0px 11px 0px inset ${primaryColor}`,
              }}
            >
              {/* Title */}
              <div className="relative">
                <h1
                  className="text-center text-4xl font-normal tracking-[0.005rem]"
                  style={{
                    color: primaryColor,
                    textShadow: `rgba(${primaryColorRgb}, 0.5) 0px 0px 9px`,
                  }}
                >
                  Overview
                </h1>
              </div>

              {/* Decorative Line */}
              <div className="relative flex w-full items-center justify-center">
                <div className="w-full">
                  <div className="h-[1px] relative w-full">
                    <svg
                      className="block w-full h-[1px]"
                      fill="none"
                      preserveAspectRatio="none"
                      viewBox="0 0 537 1"
                    >
                      <line
                        stroke={`url(#paint0_linear_nugget_${primaryColor.replace("#", "")})`}
                        strokeWidth="0.894962"
                        x2="537"
                        y1="0.552519"
                        y2="0.552519"
                      />
                      <defs>
                        <linearGradient
                          gradientUnits="userSpaceOnUse"
                          id={`paint0_linear_nugget_${primaryColor.replace("#", "")}`}
                          x1="537"
                          x2="0"
                          y1="0.999899"
                          y2="0.99992"
                        >
                          <stop stopColor={primaryColor} stopOpacity="0" />
                          <stop offset="0.484375" stopColor={primaryColor} stopOpacity="0.994792" />
                          <stop offset="1" stopColor={primaryColor} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Content Area - Adjusted for reduced height */}
              <div className="flex-1 w-full flex items-center justify-center px-1 overflow-hidden"
                style={{
                  maxHeight: 'calc(85vh - 180px)', // Adjusted for new height
                }}
              >
                <div className="w-full  mx-auto h-full"
                style={{maxWidth:'45rem'}}
                >
                  <div className="w-full h-full relative">
                    {/* Loading state */}
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg z-10">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                          <Text color="white">Loading content...</Text>
                        </div>
                      </div>
                    )}

                    {/* Error state */}
                    {iframeError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg z-10">
                        <div className="text-center p-8">
                          <Text color="red.300" fontSize="lg" mb={2}>
                            Failed to load content
                          </Text>
                          <Text color="gray.300" fontSize="sm" mb={4}>
                            Could not load: {NUGGET_URL}
                          </Text>
                          <button
                            onClick={() => {
                              setIsLoading(true);
                              setIframeError(false);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Retry
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Iframe Container */}
                    <Box
                      className="url_box"
                      w="100%"
                      h="100%"
                      borderRadius="12px"
                      overflow="hidden"
                      mx="auto"
                      position="relative"
                      border={`1px solid rgba(${primaryColorRgb}, 0.3)`}
                      boxShadow={`0 0 20px rgba(${primaryColorRgb}, 0.2)`}
                    >
                      <iframe
                        src={NUGGET_URL}
                        width="100%"
                        height="100%"
                        title="Goal Overview"
                        style={{
                          border: 'none',
                          background: 'white',
                        }}
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </Box>
                  </div>
                </div>
              </div>

              {/* Navigation - positioned at bottom */}
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between px-8">
                <div className="pointer-events-auto"
    style={{marginLeft:"-1rem"}}
    >
              {/* <div className="flex justify-between items-center w-full pt-2"> */}
                <NavigationArrow
                  direction="left"
                  onClick={handleBackNavigation}
                  primaryColor={primaryColor}
                />
                </div>
                 <div className="pointer-events-auto"
   style={{marginRight:"-1rem"}}
   >
                <NavigationArrow
                  direction="right"
                  onClick={handleNextNavigation}
                  primaryColor={primaryColor}
                  // disabled={isNavigating}
                />
              </div>
</div>
              {/* Background Glow Effect */}
              <div className="absolute h-[30.04rem] left-[11.067rem] mix-blend-lighten top-[1.294rem] w-[33.086rem] pointer-events-none">
                <div className="absolute bottom-[-102.084%] left-[-92.686%] right-[-92.686%] top-[-102.084%]">
                  <svg
                    className="block w-full h-full"
                    fill="none"
                    preserveAspectRatio="none"
                    viewBox="0 0 1512 1463"
                  >
                    <g
                      filter={`url(#filter0_f_nugget_${primaryColor.replace("#", "")})`}
                      id="Ellipse 7187"
                      opacity="0.2"
                      style={{ mixBlendMode: "lighten" }}
                    >
                      <ellipse
                        cx="755.685"
                        cy="731.318"
                        fill={primaryColor}
                        rx="264.685"
                        ry="240.319"
                      />
                    </g>
                    <defs>
                      <filter
                        colorInterpolationFilters="sRGB"
                        filterUnits="userSpaceOnUse"
                        height="1461.94"
                        id={`filter0_f_nugget_${primaryColor.replace("#", "")}`}
                        width="1510.68"
                        x="0.34726"
                        y="0.34726"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                        <feGaussianBlur result="effect1_foregroundBlur_53_81" stdDeviation="245.326" />
                      </filter>
                    </defs>
                  </svg>
                </div>
              </div>

              {/* Inset Shadow Border */}
              <div
                className="absolute inset-0 pointer-events-none rounded-[2.375rem]"
                style={{
                  boxShadow: `0px 0px 11px 0px inset rgba(${primaryColorRgb}, 0.3), 0px 0px 20px 0px rgba(${primaryColorRgb}, 0.2)`,
                }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
});

export default SkillSet;